import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type FocusEvent, type KeyboardEvent } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import type { Planet } from '@kaeliux/shared';
import { useAuthStore } from '../store/authStore';
import { usePlanetStore } from '../store/planetStore';
import { useMyAlliance } from '../hooks/useMyAlliance';
import { useTeamPlanet } from '../hooks/useTeamPlanet';
import { isTeamPlanetRoute } from '../utils/teamPlanetStyle';
import { useEscapeKey, useOutsidePointerDown } from './ui/overlayHooks';
import { INTERACTIVE_CLASSES } from './ui/buttonStyles';
import { computePopoverPlacement, type PopoverPosition } from '../utils/popoverPlacement';
import { cn } from '../utils/cn';

const MENU_MIN_WIDTH = 208;
const MENU_MAX_HEIGHT = 288;
const VIEWPORT_MARGIN = 8;

/**
 * The planet "address" in the top nav, upgraded to a switcher. With one
 * planet it's the same static thumbnail + `g:s:p` coordinates label as
 * before (the planet name lives only in the tooltip, deliberately — keeping
 * the visible text to coordinates avoids colliding with page text
 * elsewhere); with more than one planet it becomes a dropdown that changes
 * which planet the whole app is viewing/managing (via
 * `usePlanetStore.setActivePlanet`, a view change only — the profile's homeworld
 * `mainPlanetId` is untouched). The name IS shown per-planet in the dropdown
 * options. The active coordinates stay under `data-testid="current-planet-coords"`
 * so existing assertions hold.
 *
 * The alliance's Team planet, when there is one, is listed under its own
 * "Alliance" group. It is never an `activePlanetId` (the game loop only
 * ticks personal planets); picking it navigates to `/team-planet`, and on
 * those routes it is the entry shown as current.
 *
 * The list is portalled to `document.body` and placed against the button by
 * the same `computePopoverPlacement` the building popover uses: the HUD's
 * identity block clips its overflow (so a long subtitle truncates rather than
 * painting over the currency chips), and a dropdown rendered inside it was
 * clipped out of sight entirely. Being portalled, it is no longer next to the
 * button in tab order, so focus is moved into it on open and back to the
 * button when it closes from the keyboard or a pick.
 */
function coordsOf(planet: Planet): string {
    return `${planet.coordinates.galaxy}:${planet.coordinates.system}:${planet.coordinates.position}`;
}

function planetImage(planetType: number): string {
    return new URL(`../assets/images/planets/planet_${planetType}.png`, import.meta.url).href;
}

function placeMenu(anchor: HTMLElement, menu: HTMLElement | null): PopoverPosition {
    const size = menu
        ? { width: menu.offsetWidth, height: menu.offsetHeight }
        : { width: MENU_MIN_WIDTH, height: MENU_MAX_HEIGHT };
    return computePopoverPlacement(anchor.getBoundingClientRect(), size, {
        top: VIEWPORT_MARGIN,
        bottom: window.innerHeight - VIEWPORT_MARGIN,
        left: VIEWPORT_MARGIN,
        right: window.innerWidth - VIEWPORT_MARGIN,
    });
}

function optionsOf(menu: HTMLElement | null): HTMLElement[] {
    return menu ? Array.from(menu.querySelectorAll<HTMLElement>('[role="option"]')) : [];
}

export default function PlanetSwitcher() {
    const navigate = useNavigate();
    const { pathname } = useLocation();
    const profile = useAuthStore((s) => s.profile);
    const personalPlanet = usePlanetStore((s) => s.currentPlanet);
    const planets = usePlanetStore((s) => s.planets);
    const setActivePlanet = usePlanetStore((s) => s.setActivePlanet);
    const allianceQuery = useMyAlliance();
    const teamPlanet = useTeamPlanet({ enabled: !!allianceQuery.data }).data ?? null;
    const viewingTeamPlanet = isTeamPlanetRoute(pathname) && !!teamPlanet;
    const currentPlanet = viewingTeamPlanet ? teamPlanet : personalPlanet;
    const [position, setPosition] = useState<PopoverPosition | null>(null);
    const buttonRef = useRef<HTMLButtonElement>(null);
    const menuRef = useRef<HTMLUListElement>(null);
    const insideRefs = useMemo(() => [buttonRef, menuRef], []);
    const canSwitch = planets.length > 1 || !!teamPlanet;
    const open = position !== null && canSwitch;

    const close = useCallback(() => setPosition(null), []);
    const closeAndFocusButton = useCallback(() => {
        setPosition(null);
        buttonRef.current?.focus();
    }, []);
    const reposition = useCallback(() => {
        if (!buttonRef.current) return;
        const next = placeMenu(buttonRef.current, menuRef.current);
        setPosition((prev) =>
            prev && prev.left === next.left && prev.top === next.top && prev.transform === next.transform ? prev : next,
        );
    }, []);

    useEscapeKey(open, closeAndFocusButton);
    useOutsidePointerDown(open, insideRefs, close);

    useEffect(() => {
        if (!canSwitch) setPosition(null);
    }, [canSwitch]);

    useLayoutEffect(() => {
        if (!open) return;
        reposition();
        const menu = menuRef.current;
        (menu?.querySelector<HTMLElement>('[aria-selected="true"]') ?? optionsOf(menu)[0])?.focus();

        window.addEventListener('resize', reposition);
        return () => window.removeEventListener('resize', reposition);
    }, [open, reposition]);

    if (!currentPlanet) return null;

    const label = (
        <span
            className="inline-flex items-center gap-1.5 font-mono text-content-secondary"
            title={`${currentPlanet.name} — ${coordsOf(currentPlanet)}`}
        >
            <img
                src={planetImage(currentPlanet.planetType)}
                alt=""
                className="w-4 h-4 rounded-full object-cover border border-space-600 shrink-0"
            />
            <span data-testid="current-planet-coords">{coordsOf(currentPlanet)}</span>
        </span>
    );

    // A single planet has nothing to switch to — keep the plain label.
    if (!canSwitch) {
        return <span className="inline-flex items-center gap-1">· {label}</span>;
    }

    function toggle() {
        if (open || !buttonRef.current) {
            close();
            return;
        }
        setPosition(placeMenu(buttonRef.current, null));
    }

    function onMenuKeyDown(event: KeyboardEvent<HTMLUListElement>) {
        const options = optionsOf(menuRef.current);
        const index = options.indexOf(document.activeElement as HTMLElement);
        const target = event.key === 'ArrowDown'
            ? options[(index + 1) % options.length]
            : event.key === 'ArrowUp'
                ? options[(index - 1 + options.length) % options.length]
                : event.key === 'Home'
                    ? options[0]
                    : event.key === 'End'
                        ? options[options.length - 1]
                        : undefined;
        if (!target) return;
        event.preventDefault();
        target.focus();
    }

    /**
     * Closes only when focus actually moves somewhere else (Tab away). A
     * pointerdown on the list's own padding drops focus to `<body>` with no
     * `relatedTarget`; pointer dismissal belongs to `useOutsidePointerDown`.
     */
    function onMenuBlur(event: FocusEvent<HTMLUListElement>) {
        const next = event.relatedTarget as Node | null;
        if (!next || menuRef.current?.contains(next) || buttonRef.current?.contains(next)) return;
        close();
    }

    return (
        <span className="inline-flex items-center gap-1">
            <span aria-hidden="true">·</span>
            <button
                ref={buttonRef}
                type="button"
                data-testid="planet-switcher"
                aria-haspopup="listbox"
                aria-expanded={open}
                aria-label={`Switch planet — current: ${currentPlanet.name}`}
                onClick={toggle}
                className={cn('inline-flex items-center gap-1 rounded-badge px-1.5 py-0.5 hover:bg-space-700', INTERACTIVE_CLASSES)}
            >
                {label}
                <span aria-hidden="true" className="text-content-muted text-xs">▾</span>
            </button>
            {open && createPortal(
                <ul
                    ref={menuRef}
                    role="listbox"
                    aria-label="Your planets"
                    onKeyDown={onMenuKeyDown}
                    onBlur={onMenuBlur}
                    style={{
                        left: position.left,
                        top: position.top,
                        transform: position.transform,
                        maxHeight: `min(${MENU_MAX_HEIGHT}px, calc(100vh - ${2 * VIEWPORT_MARGIN}px))`,
                    }}
                    className="fixed z-50 min-w-[13rem] max-w-[calc(100vw-16px)] overflow-auto rounded-control border border-space-600 bg-space-800 shadow-e3 p-1"
                >
                    {planets.map((planet) => {
                        const isActive = !viewingTeamPlanet && planet.id === currentPlanet.id;
                        return (
                            <li key={planet.id}>
                                <button
                                    type="button"
                                    role="option"
                                    aria-selected={isActive}
                                    data-testid={`planet-option-${planet.id}`}
                                    onClick={() => {
                                        closeAndFocusButton();
                                        if (planet.id !== personalPlanet?.id && profile?.id) {
                                            setActivePlanet(planet.id, profile.id).catch(console.error);
                                        }
                                        if (viewingTeamPlanet) navigate('/planet');
                                    }}
                                    className={cn(
                                        'w-full text-left px-3 py-1.5 text-sm flex items-center justify-between gap-3 rounded-badge hover:bg-space-700',
                                        INTERACTIVE_CLASSES,
                                        isActive ? 'text-content-primary bg-space-700/60' : 'text-content-secondary',
                                    )}
                                >
                                    <span className="flex items-center gap-2 min-w-0">
                                        <img
                                            src={planetImage(planet.planetType)}
                                            alt=""
                                            className="w-5 h-5 rounded-full object-cover border border-space-600 shrink-0"
                                        />
                                        <span className="truncate">{planet.name}</span>
                                    </span>
                                    <span className="font-mono text-xs text-content-muted">{coordsOf(planet)}</span>
                                </button>
                            </li>
                        );
                    })}
                    {teamPlanet && (
                        <>
                            <li role="presentation" className="mt-1 border-t border-hairline px-3 pb-0.5 pt-2 text-micro text-content-muted">
                                Alliance
                            </li>
                            <li>
                                <button
                                    type="button"
                                    role="option"
                                    aria-selected={viewingTeamPlanet}
                                    data-testid="planet-option-team"
                                    onClick={() => {
                                        closeAndFocusButton();
                                        if (!viewingTeamPlanet) navigate('/team-planet');
                                    }}
                                    className={cn(
                                        'w-full text-left px-3 py-1.5 text-sm flex items-center justify-between gap-3 rounded-badge hover:bg-space-700',
                                        INTERACTIVE_CLASSES,
                                        viewingTeamPlanet ? 'text-content-primary bg-space-700/60' : 'text-content-secondary',
                                    )}
                                >
                                    <span className="flex items-center gap-2 min-w-0">
                                        <img
                                            src={planetImage(teamPlanet.planetType)}
                                            alt=""
                                            className="w-5 h-5 rounded-full object-cover border border-status-info/60 shrink-0"
                                        />
                                        <span className="truncate">Team Planet</span>
                                    </span>
                                    <span className="font-mono text-xs text-content-muted">{coordsOf(teamPlanet)}</span>
                                </button>
                            </li>
                        </>
                    )}
                </ul>,
                document.body,
            )}
        </span>
    );
}
